/**
 * Production-Safe Logger
 * Replaces console.* calls with environment-aware logging
 * Integrates with Sentry for error tracking in production
 */

import { getRuntimePublicConfigValue } from '@/src/lib/runtime-public-config'

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'
type SentryModule = typeof import('@sentry/nextjs')
type SentryModuleLike = SentryModule | { default: SentryModule }

let sentryModulePromise: Promise<SentryModuleLike | null> | null = null

function loadSentryModule(): Promise<SentryModuleLike | null> {
  if (sentryModulePromise) return sentryModulePromise

  sentryModulePromise = import('@sentry/nextjs').catch((error) => {
    console.warn('[Logger] Failed to load Sentry module:', error)
    sentryModulePromise = null
    return null
  })

  return sentryModulePromise
}

class Logger {
  private isDev = process.env.NODE_ENV === 'development'
  private isProduction = process.env.NODE_ENV === 'production'

  private isStaging(): boolean {
    return (
      getRuntimePublicConfigValue('NEXT_PUBLIC_ENV') === 'staging' ||
      process.env.NEXT_PUBLIC_ENV === 'staging'
    )
  }

  private shouldLog(level: LogLevel): boolean {
    // Always log errors
    if (level === 'error') return true

    // Only log in dev/staging
    return this.isDev || this.isStaging()
  }

  private withSentry(operation: (sentry: SentryModule) => void): void {
    if (!(this.isProduction || this.isStaging())) return
    void loadSentryModule().then((sentry) => {
      if (!sentry) return
      const sentryApi = 'captureException' in sentry ? sentry : sentry.default
      operation(sentryApi)
    })
  }

  /**
   * General logging (dev/staging only)
   * Arrow function to preserve 'this' context
   */
  log = (...args: unknown[]): void => {
    if (this.shouldLog('log')) {
      console.log(...args)
    }
  }

  /**
   * Info logging (dev/staging only)
   */
  info = (...args: unknown[]): void => {
    if (this.shouldLog('info')) {
      console.info(...args)
    }
  }

  /**
   * Warning logging (dev/staging only)
   * Also sends to Sentry as a breadcrumb in production
   */
  warn = (...args: unknown[]): void => {
    if (this.shouldLog('warn')) {
      console.warn(...args)
    }

    // Add as Sentry breadcrumb in production for context
    this.withSentry((Sentry) => {
      Sentry.addBreadcrumb({
        category: 'warning',
        message: args.map((arg) => String(arg)).join(' '),
        level: 'warning',
      })
    })
  }

  /**
   * Error logging (always logged)
   * Sends to Sentry in production
   */
  error = (...args: unknown[]): void => {
    console.error(...args)

    // Send to Sentry in production
    this.withSentry((Sentry) => {
      const firstArg = args[0]

      // If first argument is an Error, capture it directly
      if (firstArg instanceof Error) {
        Sentry.captureException(firstArg, {
          extra: {
            additionalContext: args.slice(1),
          },
        })
      } else {
        // Otherwise, capture as a message with context
        Sentry.captureMessage(
          args
            .map((arg) =>
              typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
            )
            .join(' '),
          {
            level: 'error',
            extra: {
              rawArgs: args,
            },
          }
        )
      }
    })
  }

  /**
   * Debug logging (dev only)
   */
  debug = (...args: unknown[]): void => {
    if (this.isDev) {
      console.debug(...args)
    }
  }

  /**
   * Group logging (dev/staging only)
   */
  group = (label: string): void => {
    if (this.shouldLog('log')) {
      console.group(label)
    }
  }

  groupEnd = (): void => {
    if (this.shouldLog('log')) {
      console.groupEnd()
    }
  }

  /**
   * Table logging (dev/staging only)
   */
  table = (data: unknown): void => {
    if (this.shouldLog('log')) {
      console.table(data)
    }
  }

  /**
   * Performance timing (dev/staging only)
   */
  time = (label: string): void => {
    if (this.shouldLog('log')) {
      console.time(label)
    }
  }

  timeEnd = (label: string): void => {
    if (this.shouldLog('log')) {
      console.timeEnd(label)
    }
  }

  /**
   * Set user context for Sentry
   * Call this after user authentication
   */
  setUser = (
    user: { id: string; email?: string; name?: string } | null
  ): void => {
    this.withSentry((Sentry) => {
      if (!user) {
        Sentry.setUser(null)
        return
      }
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      })
    })
  }

  /**
   * Add a breadcrumb for debugging context
   */
  breadcrumb = (
    message: string,
    category: string = 'custom',
    data?: Record<string, unknown>
  ): void => {
    this.withSentry((Sentry) => {
      Sentry.addBreadcrumb({
        category,
        message,
        data,
        level: 'info',
      })
    })
  }
}

export const logger = new Logger()
